import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";

import dfAddDays from "date-fns/addDays";
import dfSetHours from "date-fns/setHours";
import dfSetMinutes from "date-fns/setMinutes";
import dfSetSeconds from "date-fns/setSeconds";
import dfSubDays from "date-fns/subDays";

import { formatDate, getDateByTimeZone } from "@/lib/date";

import DelayedDelivery from "@/Models/DelayedDelivery";
import api from "../Api";

export const QUERY_SORT = {
  DELAYED_REASON_AT: "delayedReasonAt",
  COMMENT_CREATED_AT: "commentCreatedAt",
  UNCONFIRMED_COMMENT: "unconfirmedComment",
};

export const DELIVERY_STATUS_FILTER = {
  IN_PROGRESS: "inProgress",
  COMPLETED: "completed",
  SENT_BACK: "sentBack",
  LOST: "lost",
  CANCELED: "canceled",
};

export const CONSTANTS = {
  FETCH_ALL: "FETCH_ALL",
  SET_QUERY: "SET_QUERY",
};

const zonedNow = getDateByTimeZone();
const initDateFrom = formatDate(dfSubDays(zonedNow, 1));
const initDateTo = formatDate(dfAddDays(zonedNow, 1));

const INITIAL_QUERY = {
  delayedReasonAtFrom: initDateFrom,
  delayedReasonAtTo: initDateTo,
  bookId: "",
  receiverName: "",
  receiverMobile: "",
  deliveryStatus: undefined,
  returnReview: undefined,
  page: 1,
  pageSize: 20,
  sort: QUERY_SORT.DELAYED_REASON_AT,
};

const INITIAL_STATE = {
  rows: [],
  totalCount: 0,
  pageCount: 0,
  query: INITIAL_QUERY,
};

const reducer = (state, action) => {
  switch (action.type) {
    case CONSTANTS.FETCH_ALL:
      return {
        ...state,
        rows: action.rows,
        totalCount: action.totalCount,
        pageCount: action.pageCount,
      };
    case CONSTANTS.SET_QUERY:
      return {
        ...state,
        query: {
          ...state.query,
          ...action.query,
        },
      };
    default:
      return INITIAL_STATE;
  }
};

const compactQuery = (query = {}) => {
  const next = {};

  Object.keys(query).forEach((key) => {
    const value = query[key];

    if (value === undefined || value === null || value === "") {
      return;
    }

    next[key] = value;
  });

  return next;
};

export const getNextAutoReturnAt = (now = getDateByTimeZone()) => {
  const daysToAdd = now.getHours() < 15 ? 1 : 2;
  let next = dfAddDays(now, daysToAdd);

  next = dfSetHours(next, 15);
  next = dfSetMinutes(next, 0);
  next = dfSetSeconds(next, 0);

  return next;
};

export const formatNextAutoReturnAt = (now) => {
  return formatDate(getNextAutoReturnAt(now), "yyyy-MM-dd HH:mm");
};

export const Context = createContext(INITIAL_STATE);

export const Provider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  return (
    <Context.Provider value={{ dispatch, state }}>{children}</Context.Provider>
  );
};

export const useDelayedDeliveriesStore = () => {
  const { dispatch, state } = useContext(Context);

  const fetchAll = useCallback(
    async (queryOverride) => {
      const query = compactQuery(queryOverride || state.query);
      const { rows, totalCount } = await api.get("/delayed-deliveries", query);
      const items = (rows || []).map((d) => new DelayedDelivery(d));

      dispatch({
        type: CONSTANTS.FETCH_ALL,
        rows: items,
        totalCount: totalCount || 0,
        pageCount:
          Math.floor((totalCount || 0) / (query.pageSize || 20)) +
          ((totalCount || 0) % (query.pageSize || 20) ? 1 : 0),
      });
    },
    [dispatch, state.query],
  );

  const returnApprove = useCallback(async (bookId) => {
    return await api.post(
      `/delayed-deliveries/${encodeURIComponent(bookId)}/return-approve`,
    );
  }, []);

  const returnWait = useCallback(async (bookId) => {
    return await api.post(
      `/delayed-deliveries/${encodeURIComponent(bookId)}/return-wait`,
    );
  }, []);

  const setQuery = useCallback(
    (query = {}) => {
      dispatch({ type: CONSTANTS.SET_QUERY, query });
    },
    [dispatch],
  );

  return {
    state,
    fetchAll,
    returnApprove,
    returnWait,
    setQuery,
  };
};
